import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';

export const PHASES_RETAINED_READ_TIMEOUT_MS = 30_000;
export class PhasesEvidenceTimeoutError extends Error {
  constructor() { super('Phases retained-information read timed out'); this.name = 'PhasesEvidenceTimeoutError'; }
}

export const PHASES_EVIDENCE_SHEETS = ['USD', 'GBP', 'EUR', 'JPY', 'AUD', 'BLOC'] as const;
export interface PhasesEvidence {
  field_id: string; label: string; origin: string; state: string; value: unknown;
  native_value?: unknown; unit: string | null; frequency: string | null;
  reference_period: string | null; publication_at: string | null;
  source_update_at?: string | null; source_edition_at?: string | null;
  publication_state: string; retrieved_at: string | null;
  source_url: string | null; source_urls?: string[]; publisher: string | null;
  series_id: unknown; history_count: number; history_state: string;
  history_origin?: string; reason: string | null; current_eligible: boolean;
  raw_record?: Record<string, unknown> | null; raw_records?: Record<string, unknown>[];
  native_currency?: string | null; native_magnitude?: unknown; measurement_basis?: string | null;
  currency_assignment?: string; currency_exposure?: Record<string, unknown>;
  reporting_basis?: unknown; history_requirement?: Record<string, unknown>;
  retained_observation_state?: string;
  latest_retained_observation?: {
    native_value: unknown; reference_period: string | null; field_id: string;
    origin: string; material_hash?: string | null; unit?: string | null;
    series_id?: unknown; source_url?: string | null; publisher?: string | null;
    publication_at?: string | null; source_update_at?: string | null;
    source_edition_at?: string | null; observed_at?: string | null; effective_at?: string | null;
    retrieved_at?: string | null; recorded_at?: string | null; received_at?: string | null;
    history_origin?: string; history_route?: string;
    provenance?: Record<string, unknown>;
  } | null;
}
export const PHASES_DASHBOARD_COLUMNS = ['phase', 'policy', 'prices', 'growth', 'external', 'positioning',
  'risk', 'strongest_block', 'activation', 'state', 'cycle', 'weeks'] as const;
export interface PhasesDashboard {
  schema: 'mack_phases_dashboard_v1';
  country_rows: {
    sheet: string; display_label: string; formula_state: 'FORMULA_PASS_2_PENDING';
    columns: Record<typeof PHASES_DASHBOARD_COLUMNS[number], null>;
    growth_evidence: PhasesEvidence | null; inflation_evidence: PhasesEvidence | null;
    history_coverage: { growth: Record<string, unknown> | null; inflation: Record<string, unknown> | null };
  }[];
  participants: {
    participant: string; label: string;
    units: { sheet: string; contract_id: string; methodology_input: null; calculated_M: null;
      methodology_state: string; output_state: string }[];
  }[];
  dealers: { state: string; reason: string; [key: string]: unknown };
  [key: string]: unknown;
}
export interface PhasesInformationField {
  field_id: string; display_label: string; sheet: string; block: string | null;
  field_class: string; current_availability_state: string; blocker: string | null;
  raw_input_ids: string[];
}
export interface PhasesOwnedSource {
  contract_id: string; field_id: string; sheet: string; participant: string;
  availability_state: string; blocker: string | null;
  requirement_category?: 'A' | 'B' | 'C'; source_identity_resolved?: boolean;
  owner_decision_ids?: string[];
}
export interface PhasesInformationProjection {
  success: true; schema: 'mack_phases_information_projection_v1';
  formula_execution: false; phase_outputs_enabled: false; source_fetches: 0; writer_entries: 0;
  generated_at: string; T1_installation: Record<string, unknown>;
  dashboard?: PhasesDashboard;
  counts: {
    fields: number; raw_leaves: number; phases_owned: number; eligible_sources: number;
    upstream_inputs?: number; upstream_calculator?: number;
    upstream_counts?: { inputs_declared?: number; calculator_declared?: number };
    visible_UI_business_value_slots?: number; hidden_consumed_delta_slots?: number;
    business_and_consumed_value_positions?: number; inventory_rows?: number;
    contract_metadata_records?: number; raw_external_leaves?: number;
  };
  sheets: {
    sheet: string; display_label: string; raw_input_ids: string[]; blocks: unknown;
    formula_state: 'FORMULA_PASS_2_PENDING'; phase: null; delta_growth: null;
    delta_inflation: null; activation: null; reactivity_state: null; cycle: null; cycle_weeks: null;
  }[];
  evidence: PhasesEvidence[]; calendar_context: Record<string, unknown>[];
  owner_approved_rulings?: Record<string, unknown>;
  trend_requirements?: Record<string, unknown>[];
  owned_sources: PhasesOwnedSource[]; fields: PhasesInformationField[];
  pulse_lifecycle: {
    declared: number; authority_eligible: number; resident_timed_jobs: number | null;
    duplicate_identities: number; plans: Record<string, unknown>[];
  };
}
export interface PhasesEvidenceHistory {
  success: true; field_id: string; origin: string; total: number;
  next_offset: number | null; rows: Record<string, unknown>[];
}

export interface PhasesUnit {
  sheet: string;
  participant: string;
  contract_id: string;
}
export function decodePhasesUnitIdentities(raw: unknown): PhasesUnit[] {
  const p = raw as { success?: boolean; schema?: string; units?: PhasesUnit[];
    formula_execution?: boolean; source_fetches?: number; writer_entries?: number;
    t1_records_served?: number; t2_records_served?: number };
  if (!p || p.success !== true || p.schema !== 'mack_phases_unit_identities_v1' ||
      p.formula_execution !== false || p.source_fetches !== 0 || p.writer_entries !== 0 ||
      p.t1_records_served !== 0 || p.t2_records_served !== 0 || !Array.isArray(p.units) || p.units.length !== 60 ||
      p.units.some(unit => !unit || Object.keys(unit).length !== 3 ||
        typeof unit.sheet !== 'string' || !unit.sheet || typeof unit.participant !== 'string' || !unit.participant ||
        typeof unit.contract_id !== 'string' || !unit.contract_id) ||
      new Set(p.units.map(unit => JSON.stringify([unit.sheet, unit.participant, unit.contract_id]))).size !== 60) {
    throw new Error('Phases mapped identities unavailable');
  }
  return p.units;
}

export async function fetchPhasesUnitIdentities(token?: string | null): Promise<PhasesUnit[]> {
  const response = await axios.get(`${apiBase()}/mack/phases`, {
    headers: { Authorization: `Bearer ${token || authToken()}` },
  });
  return decodePhasesUnitIdentities(response.data);
}
export interface PhasesSelectedInformation {
  success: true;
  schema: 'mack_phases_selected_information_v1';
  unit: PhasesUnit;
  availability_state: string;
  native_value: null;
  receipt: Record<string, unknown> | null;
  supporting_source_contracts: Record<string, unknown>[];
  field_role: 'FORMULA_PASS2_METHOD_INPUT_PENDING';
  methodology_state: 'FORMULA_PASS2_METHOD_INPUT_PENDING';
  numerical_authority: 'NOT_YET_SUPPLIED';
  pass1_source_route: null;
  pass1_pulse_route: null;
  pass1_economic_write_authorized: false;
  raw_evidence_field_role?: 'FORMULA_PASS2_METHOD_INPUT_PENDING';
  raw_evidence_state?: string;
  raw_evidence_blocker?: string | null;
  participant_value_field_id?: string;
  participant_value_field_role?: 'FORMULA_PASS2_OUTPUT';
  participant_value_state?: 'FORMULA_PASS_2_PENDING';
  formula_execution: false;
  source_fetches: 0;
  writer_entries: 0;
}

export function decodePhasesSelectedInformation(raw: unknown, requested: PhasesUnit): PhasesSelectedInformation {
  const p = raw as PhasesSelectedInformation;
  if (!p || p.success !== true || p.schema !== 'mack_phases_selected_information_v1' ||
      !p.unit || p.unit.sheet !== requested.sheet || p.unit.participant !== requested.participant ||
      p.unit.contract_id !== requested.contract_id || p.native_value !== null ||
      p.formula_execution !== false || p.source_fetches !== 0 || p.writer_entries !== 0 ||
      typeof p.availability_state !== 'string' || !Array.isArray(p.supporting_source_contracts)) {
    throw new Error('Selected Phases information unavailable');
  }
  if (p.field_role !== 'FORMULA_PASS2_METHOD_INPUT_PENDING' ||
      p.methodology_state !== 'FORMULA_PASS2_METHOD_INPUT_PENDING' ||
      p.availability_state !== 'FORMULA_PASS2_METHOD_INPUT_PENDING' ||
      p.numerical_authority !== 'NOT_YET_SUPPLIED' || p.pass1_source_route !== null ||
      p.pass1_pulse_route !== null || p.pass1_economic_write_authorized !== false ||
      p.participant_value_state !== 'FORMULA_PASS_2_PENDING' ||
       p.participant_value_field_role !== 'FORMULA_PASS2_OUTPUT' ||
       p.participant_value_field_id !== requested.contract_id.replace('mack.phases.raw.', 'mack.phases.capital.').replace(/\.capital_value$/, '.M') ||
       p.raw_evidence_field_role !== 'FORMULA_PASS2_METHOD_INPUT_PENDING' ||
       p.raw_evidence_state !== p.availability_state) {
    throw new Error('Phases methodology and calculated-output roles refused');
  }
  return p;
}

export async function fetchPhasesSelectedInformation(unit: PhasesUnit, token?: string | null): Promise<PhasesSelectedInformation> {
  const response = await axios.get(`${apiBase()}/mack/phases`, {
    params: unit,
    headers: { Authorization: `Bearer ${token || authToken()}` },
  });
  return decodePhasesSelectedInformation(response.data, unit);
}

const nonnegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;
const identityList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(identity => typeof identity === 'string' && identity.length > 0) &&
  new Set(value).size === value.length;

export function decodePhasesInformationProjection(raw: unknown): PhasesInformationProjection {
  const p = raw as PhasesInformationProjection;
  if (!p || p.success !== true || p.schema !== 'mack_phases_information_projection_v1' ||
      p.formula_execution !== false || p.phase_outputs_enabled !== false ||
      p.source_fetches !== 0 || p.writer_entries !== 0 || !p.T1_installation || !p.counts ||
      !Array.isArray(p.sheets) || p.sheets.length !== PHASES_EVIDENCE_SHEETS.length ||
      !Array.isArray(p.evidence) || !Array.isArray(p.fields) || !Array.isArray(p.owned_sources) ||
      !Array.isArray(p.calendar_context) || !p.pulse_lifecycle || !Array.isArray(p.pulse_lifecycle.plans)) {
    throw new Error('Phases information projection unavailable');
  }
  const sheets = new Set<string>(PHASES_EVIDENCE_SHEETS);
  p.sheets.forEach(sheet => {
    if (!sheet || !sheets.delete(sheet.sheet) || !identityList(sheet.raw_input_ids) ||
        sheet.formula_state !== 'FORMULA_PASS_2_PENDING' ||
        ['phase', 'delta_growth', 'delta_inflation', 'activation', 'reactivity_state', 'cycle', 'cycle_weeks']
          .some(key => sheet[key as keyof typeof sheet] !== null)) {
      throw new Error('Phases formula output or sheet identity refused');
    }
  });
  const fieldIds = new Set<string>();
  p.fields.forEach(field => {
    if (!field || typeof field.field_id !== 'string' || !field.field_id || fieldIds.has(field.field_id) ||
        !identityList(field.raw_input_ids)) throw new Error('Phases field coverage refused');
    fieldIds.add(field.field_id);
  });
  const evidenceIds = new Set<string>();
  p.evidence.forEach(field => {
    if (!field || typeof field.field_id !== 'string' || !field.field_id || evidenceIds.has(field.field_id) ||
        !nonnegativeInteger(field.history_count) || typeof field.current_eligible !== 'boolean' ||
        (typeof field.value === 'number' && !Number.isFinite(field.value))) {
      throw new Error('Phases raw evidence identity/value refused');
    }
    evidenceIds.add(field.field_id);
  });
  const contractIds = new Set<string>();
  const ownedFields = new Set<string>();
  p.owned_sources.forEach(source => {
    if (!source || typeof source.contract_id !== 'string' || !source.contract_id ||
        contractIds.has(source.contract_id) || typeof source.field_id !== 'string' ||
        ownedFields.has(source.field_id) || !PHASES_EVIDENCE_SHEETS.some(sheet => sheet === source.sheet)) {
      throw new Error('Phases source identity refused');
    }
    contractIds.add(source.contract_id); ownedFields.add(source.field_id);
  });
  if (!nonnegativeInteger(p.counts.fields) || p.counts.fields !== p.fields.length ||
      !nonnegativeInteger(p.counts.raw_leaves) || !nonnegativeInteger(p.counts.phases_owned) ||
      p.counts.phases_owned !== p.owned_sources.length || !nonnegativeInteger(p.counts.eligible_sources) ||
      !nonnegativeInteger(p.pulse_lifecycle.declared) || !nonnegativeInteger(p.pulse_lifecycle.authority_eligible) ||
      !nonnegativeInteger(p.pulse_lifecycle.duplicate_identities) ||
      (p.pulse_lifecycle.resident_timed_jobs !== null && !nonnegativeInteger(p.pulse_lifecycle.resident_timed_jobs))) {
    throw new Error('Phases retained coverage/counts refused');
  }
  if (p.dashboard !== undefined) decodePhasesDashboard(p.dashboard);
  return p;
}

export async function fetchPhasesInformation(token?: string | null, signal?: AbortSignal): Promise<PhasesInformationProjection> {
  const response = await axios.get(`${apiBase()}/mack/phases`, {
    params: { view: 'dashboard' },
    headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    signal, timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodePhasesInformationProjection(response.data);
}

export function decodePhasesDashboard(raw: unknown): PhasesDashboard {
  const dashboard = raw as PhasesDashboard;
  const sheets = new Set<string>(PHASES_EVIDENCE_SHEETS);
  if (!dashboard || dashboard.schema !== 'mack_phases_dashboard_v1' ||
      !Array.isArray(dashboard.country_rows) || dashboard.country_rows.length !== 6 ||
      !Array.isArray(dashboard.participants) || dashboard.participants.length !== 10 ||
      !dashboard.dealers || typeof dashboard.dealers.state !== 'string' || typeof dashboard.dealers.reason !== 'string') {
    throw new Error('Phases dashboard projection unavailable');
  }
  for (const row of dashboard.country_rows) {
    if (!row || !sheets.delete(row.sheet) || row.formula_state !== 'FORMULA_PASS_2_PENDING' || !row.columns ||
        PHASES_DASHBOARD_COLUMNS.some(column => row.columns[column] !== null) || !row.history_coverage) {
      throw new Error('Phases dashboard calculated values refused');
    }
  }
  const participants = new Set<string>();
  const contracts = new Set<string>();
  for (const participant of dashboard.participants) {
    if (!participant || !participant.participant || participants.has(participant.participant) || !participant.label ||
        !Array.isArray(participant.units) || participant.units.length !== 6) throw new Error('Phases installed participant roster refused');
    participants.add(participant.participant);
    const unitSheets = new Set<string>(PHASES_EVIDENCE_SHEETS);
    for (const unit of participant.units) {
      if (!unit || !unitSheets.delete(unit.sheet) || !unit.contract_id || contracts.has(unit.contract_id) ||
          unit.methodology_input !== null || unit.calculated_M !== null ||
          unit.methodology_state !== 'FORMULA_PASS2_METHOD_INPUT_PENDING' || unit.output_state !== 'FORMULA_PASS_2_PENDING') {
        throw new Error('Phases methodology or calculated monetary values refused');
      }
      contracts.add(unit.contract_id);
    }
  }
  return dashboard;
}

export async function fetchPhasesHistory(field: PhasesEvidence, token?: string | null, offset = 0): Promise<PhasesEvidenceHistory> {
  const origin = field.history_origin || field.origin;
  const response = await axios.get(`${apiBase()}/mack/phases/history`, {
    params: { field_id: field.field_id, origin, offset, limit: 30 },
    headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  const data = response.data as PhasesEvidenceHistory;
  if (!data || data.success !== true || data.field_id !== field.field_id || data.origin !== origin ||
      !Array.isArray(data.rows) || !nonnegativeInteger(data.total) ||
      (data.next_offset !== null && (!nonnegativeInteger(data.next_offset) || data.next_offset <= offset))) {
    throw new Error('Phases evidence history unavailable');
  }
  return data;
}

export function phasesRequestFailure(error: unknown): string {
  if (error instanceof PhasesEvidenceTimeoutError || (axios.isAxiosError(error) &&
      ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code || ''))) {
    return 'The retained-information read timed out. No acquisition was requested. Refresh retained information to try the read again.';
  }
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    return 'Your session is unauthenticated or has expired. Sign in again to read retained Phases information.';
  }
  if (axios.isAxiosError(error) && error.response?.status === 403) {
    return 'This authenticated session does not have access to retained Phases information.';
  }
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return 'Phases evidence endpoint awaiting activation.';
  }
  if (axios.isAxiosError(error) && !error.response) {
    return 'No backend response was received for retained Phases information. Refresh retained information to check the connection.';
  }
  return 'Retained Phases information could not be read. Refresh retained information to try again; no acquisition was requested.';
}
